import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Loader2, Search } from 'lucide-react'
import { useMemo, useState } from 'react'

import { listKaloriShops } from '@/api/services/nutrition'
import type { KaloriShop } from '@/api/types/nutrition'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

type Props = {
  selectedShop: KaloriShop | null
  onSelect: (shop: KaloriShop | null) => void
  onBack: () => void
}

export function ShopPickerView({ selectedShop, onSelect, onBack }: Props) {
  const [filter, setFilter] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['nutrition', 'kalori-shops'],
    queryFn: listKaloriShops,
    staleTime: 24 * 60 * 60 * 1000,
  })

  const filteredGroups = useMemo(() => {
    if (!data) return []
    const needle = filter.trim().toLowerCase()
    if (!needle) return data.groups
    return data.groups
      .map((g) => ({
        ...g,
        shops: g.shops.filter((s) => s.name.toLowerCase().includes(needle) || s.query.toLowerCase().includes(needle)),
      }))
      .filter((g) => g.shops.length > 0)
  }, [data, filter])

  const handleSelect = (shop: KaloriShop) => {
    // 選択済みを再タップ → 解除、それ以外 → 選択
    onSelect(selectedShop?.query === shop.query ? null : shop)
    onBack()
  }

  return (
    <div className="space-y-2">
      {/* ヘッダ: 戻るボタン */}
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="sm" className="h-8 gap-1 px-2" onClick={onBack}>
          <ArrowLeft className="size-3.5" />
          <span className="text-xs">検索に戻る</span>
        </Button>
      </div>

      {/* 絞り込み入力 */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="店名または slug で絞り込み..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="h-8 pl-8 text-sm"
        />
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" />
          読み込み中...
        </div>
      )}

      {!isLoading && filteredGroups.length === 0 && (
        <p className="py-4 text-center text-xs text-muted-foreground">
          {filter ? '一致するショップが見つかりませんでした' : 'ショップ一覧が取得できません'}
        </p>
      )}

      <div className="max-h-[50vh] space-y-3 overflow-y-auto">
        {filteredGroups.map((group) => (
          <section key={group.name} className="space-y-1">
            <h4 className="py-0.5 text-[11px] font-semibold text-muted-foreground">{group.name}</h4>
            <div className="grid grid-cols-2 gap-1">
              {group.shops.map((shop) => {
                const isSelected = selectedShop?.query === shop.query
                return (
                  <button
                    key={shop.query}
                    type="button"
                    onClick={() => handleSelect(shop)}
                    className={
                      'flex items-center rounded-md border px-2 py-1.5 text-left text-xs transition-colors ' +
                      (isSelected
                        ? 'border-emerald-500 bg-emerald-500/10 text-emerald-500'
                        : 'border-border hover:bg-accent/50')
                    }
                  >
                    <span className="truncate">{shop.name}</span>
                  </button>
                )
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}
